import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Box,
  Card,
  Tabs,
  Tab,
  Typography,
  Skeleton,
} from '@mui/material';
import { Download, Print, Email, PictureAsPdf } from '@mui/icons-material';
import Button from '@/components/CustomButtons/Button';
import { Document, Page } from 'react-pdf';
import { RefundsApi } from '@/api/clients/refunds/refunds.api';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import { modalStyle } from '@/views/Clients/Claim';
import { useDownloadECheck } from '@/api/hooks/refunds';
import { ENTITY_TYPE } from '@/utility';
import { useConfirmation } from '@/context/ConfirmationContext';
import ECheckAuditTimeline from './ECheckAuditTimeline';

export default function ECheckPreviewModal({ open, onClose, refund, reFetch }) {
  const { t } = useTranslation();
  const {
    downloadECheck,
    fetchECheckPdf,
    clearPdf,
    printPdf,
    pdfPreview,
    downloadLoading,
    previewLoading,
    printLoading,
  } = useDownloadECheck();
  const [pageNumber, setPageNumber] = useState(1);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { requestConfirmation } = useConfirmation();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const payeeName =
    refund?.entity_type === ENTITY_TYPE.BUSINESS
      ? refund?.business_payee_name
      : refund?.first_name + ' ' + refund?.last_name;

  useEffect(() => {
    if (open && refund?.transaction_id) {
      fetchECheckPdf(refund.transaction_id).then((result) => {
        if (result?.success && reFetch) {
          reFetch();
        }
      });
    }

    return () => {
      clearPdf();
      setPageNumber(1);
    };
  }, [open, refund?.transaction_id, fetchECheckPdf, clearPdf, reFetch]);

  const handleDownload = () => {
    requestConfirmation(
      t(
        'eCheckPreview.confirmDownload',
        'Are you sure you want to download this eCheck?',
      ),
      async (comment) => {
        const result = await downloadECheck(refund.transaction_id, comment);
        if (result?.success && reFetch) {
          reFetch();
        }
      },
      null,
      {
        showCommentField: true,
        commentLabel: t('eCheckPreview.commentLabel', 'Reason for download'),
        commentPlaceholder: t(
          'eCheckPreview.commentPlaceholder',
          'Enter reason...',
        ),
        commentRequired: true,
      },
    );
  };

  const handlePrint = () => {
    requestConfirmation(
      t(
        'eCheckPreview.confirmPrint',
        'Are you sure you want to print this eCheck?',
      ),
      async (comment) => {
        const result = await printPdf(refund.transaction_id, comment);
        if (result?.success && reFetch) {
          reFetch();
        }
      },
      null,
      {
        showCommentField: true,
        commentLabel: t('eCheckPreview.commentLabel', 'Reason for printing'),
        commentPlaceholder: t(
          'eCheckPreview.commentPlaceholder',
          'Enter reason...',
        ),
        commentRequired: true,
      },
    );
  };

  const handleSendEmail = () => {
    requestConfirmation(
      t(
        'eCheckPreview.confirmEmail',
        'Are you sure you want to send this eCheck via email?',
      ),
      async (comment) => {
        setSendingEmail(true);
        try {
          await RefundsApi.sendECheckEmail(refund.transaction_id, comment);
          if (reFetch) {
            reFetch();
          }
        } catch (err) {
          console.error('Error sending eCheck email:', err);
        } finally {
          setSendingEmail(false);
        }
      },
      null,
      {
        showCommentField: true,
        commentLabel: t(
          'eCheckPreview.commentLabel',
          'Reason for sending email',
        ),
        commentPlaceholder: t(
          'eCheckPreview.commentPlaceholder',
          'Enter reason...',
        ),
        commentRequired: true,
      },
    );
  };

  function onDocumentLoadSuccess() {
    setPageNumber(1);
  }

  const handleClose = () => {
    clearPdf();
    setPageNumber(1);
    setActiveTab(0);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={modalStyle}
        className='makeStyles-card-96'
        noValidate
        autoComplete='off'>
        <CardHeader style={{ transform: 'translateY(-50%)' }} color='primary'>
          <h4>{t('eCheckPreview.title', 'E-check Preview')}</h4>
        </CardHeader>
        <CardBody>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label={t('eCheckPreview.tabs.preview', 'Preview')} />
            <Tab label={t('eCheckPreview.tabs.activity', 'Activity Log')} />
          </Tabs>

          {activeTab === 0 && (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: '20px',
                  marginBottom: '20px',
                }}>
                <Card
                  style={{
                    display: 'inline-block',
                    overflow: 'hidden',
                    width: '250px',
                  }}>
                  {previewLoading ? (
                    <Skeleton
                      variant='rectangular'
                      width={250}
                      height={323}
                      animation='wave'
                    />
                  ) : pdfPreview ? (
                    <Document
                      file={pdfPreview}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={
                        <Skeleton
                          variant='rectangular'
                          width={250}
                          height={323}
                          animation='wave'
                        />
                      }
                      error={
                        <div
                          style={{
                            padding: '20px',
                            color: 'red',
                            width: '250px',
                          }}>
                          {t('eCheckPreview.renderError', 'Failed to load PDF')}
                        </div>
                      }>
                      <Page pageNumber={pageNumber} width={250} />
                    </Document>
                  ) : (
                    <div
                      style={{
                        width: '250px',
                        height: '323px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                      }}>
                      <PictureAsPdf
                        style={{ fontSize: '80px', color: '#999' }}
                      />
                    </div>
                  )}
                </Card>

                <Box sx={{ flex: 1, pt: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      {t('eCheckPreview.labels.payeeName', 'Payee Name')}
                    </Typography>
                    <Typography variant='body1' fontWeight='medium'>
                      {payeeName}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      {t('eCheckPreview.labels.memo', 'Memo')}
                    </Typography>
                    <Typography variant='body1' fontWeight='medium'>
                      {refund?.memo || '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      {t('eCheckPreview.labels.checkNumber', 'Check Number')}
                    </Typography>
                    <Typography variant='body1' fontWeight='medium'>
                      {refund?.check_number || '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 4, mt: 3 }}>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        {t('eCheckPreview.labels.downloads', 'Downloads')}
                      </Typography>
                      <Typography variant='h6' fontWeight='bold'>
                        {refund?.echeck_download_count || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        {t('eCheckPreview.labels.prints', 'Prints')}
                      </Typography>
                      <Typography variant='h6' fontWeight='bold'>
                        {refund?.echeck_print_count || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        {t('eCheckPreview.labels.emails', 'Emails')}
                      </Typography>
                      <Typography variant='h6' fontWeight='bold'>
                        {refund?.echeck_email_count || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  marginTop: '30px',
                }}>
                <Button
                  color='primary'
                  onClick={handleDownload}
                  disabled={downloadLoading}>
                  <Download
                    style={{
                      marginRight: '5px',
                      fontSize: '18px',
                      color: 'white',
                    }}
                  />
                  {downloadLoading
                    ? t('eCheckPreview.downloading', 'Downloading...')
                    : t('eCheckPreview.download', 'Download')}
                </Button>
                <Button
                  color='primary'
                  onClick={handlePrint}
                  disabled={printLoading}>
                  <Print
                    style={{
                      marginRight: '5px',
                      fontSize: '18px',
                      color: 'white',
                    }}
                  />
                  {printLoading
                    ? t('eCheckPreview.printing', 'Printing...')
                    : t('eCheckPreview.print', 'Print')}
                </Button>
                <Button
                  color='info'
                  onClick={handleSendEmail}
                  disabled={sendingEmail}>
                  <Email
                    style={{
                      marginRight: '5px',
                      fontSize: '18px',
                      color: 'white',
                    }}
                  />
                  {sendingEmail
                    ? t('eCheckPreview.sending', 'Sending...')
                    : t('eCheckPreview.sendEmail', 'Send Email')}
                </Button>
              </div>
            </>
          )}

          {activeTab === 1 && (
            <Box sx={{ height: '500px', overflowY: 'auto', py: 2 }}>
              <ECheckAuditTimeline transactionId={refund?.transaction_id} />
            </Box>
          )}
        </CardBody>
      </Box>
    </Modal>
  );
}
